/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import { defineMessages } from 'react-intl';

import {
  AlertTableDefaultColumnNames,
  Card,
  CardTypes,
  ColumnsStateType,
} from '../types/dashboardTypes';

import { FormatMessageType, SortDirection } from 'CommonTypes';
import { DataItemType } from 'EntitiesTypes';
import {
  isDataItemCategoryAggreator,
  isDataItemDerived,
  isDataItemRawMetric,
} from 'dataFormatterFunctions';

export const dashboardUtilsMessages = defineMessages({
  time: 'Time',
  name: 'Name',
  source: 'Source',
  owner: 'Owner',
  severity: 'Severity',
  status: 'Status',
  workorder: 'Service Request',
  deviceId: 'Device ID',
  deviceAlias: 'Device alias',
  errorDataItemNotFound: 'Dataitem `{dataItemName}` does not exist',
  errorRawMetricWithGroupBy:
    'V2 raw metric `{dataItemName}` must have aggregation method applied while using groupBy',
  errorDerivedMetricWithGroupBy:
    'V2 derived metric `{dataItemName}` must have aggregation method applied while using groupBy',
  errorStreamingAggregatedMetricWithGroupBy:
    'Streaming aggregated metric `{dataItemName}` cannot be shown with groupBy',
});

/**
 * Get default column state for the alert card
 * @param {function} formatMessage i18n format message
 * @param {boolean} isHierarchyDashboard set true for all hierarchy level resources
 * @returns {ColumnsStateType[]} array of default column state
 */
export function getAlertCardDefaultColumsState(
  formatMessage: FormatMessageType,
  isHierarchyDashboard: boolean
): Array<ColumnsStateType> {
  return [
    {
      id: AlertTableDefaultColumnNames.TIMESTAMP,
      columnName: formatMessage(dashboardUtilsMessages.time),
      isVisible: true,
      sortDirection: SortDirection.DESC,
    },
    {
      id: AlertTableDefaultColumnNames.NAME,
      columnName: formatMessage(dashboardUtilsMessages.name),
      isVisible: true,
    },
    {
      id: AlertTableDefaultColumnNames.SOURCE,
      columnName: formatMessage(dashboardUtilsMessages.source),
      isVisible: true,
    },
    {
      id: AlertTableDefaultColumnNames.OWNER,
      columnName: formatMessage(dashboardUtilsMessages.owner),
      isVisible: true,
    },
    {
      id: AlertTableDefaultColumnNames.SEVERITY,
      columnName: formatMessage(dashboardUtilsMessages.severity),
      isVisible: true,
    },
    {
      id: AlertTableDefaultColumnNames.STATUS,
      columnName: formatMessage(dashboardUtilsMessages.status),
      isVisible: true,
    },
    {
      id: AlertTableDefaultColumnNames.WORKORDER,
      columnName: formatMessage(dashboardUtilsMessages.workorder),
      isVisible: true,
    },
    {
      id: AlertTableDefaultColumnNames.DEVICEID,
      columnName: formatMessage(dashboardUtilsMessages.deviceId),
      isVisible: !isHierarchyDashboard,
    },
    {
      id: AlertTableDefaultColumnNames.DEVICEALIAS,
      columnName: formatMessage(dashboardUtilsMessages.deviceAlias),
      isVisible: !isHierarchyDashboard,
    },
  ];
}

/**
 * validates card attributes for V2 raw, derived or aggregated metrics while using groupBy
 * @param {object} card  card JSON properties
 * @param {object} dataItems list of card data items
 * @param formatMessage i18n formatMessage function
 * @returns {string|undefined} if any card error then error else undefined
 */
export const validateCardAttributesWithGroupBy = (
  card: Card,
  dataItems: Array<DataItemType>,
  formatMessage: FormatMessageType
): string => {
  const errorMessage = [];
  const { type, dataSource } = card;
  const { groupBy, attributes } = dataSource || {};

  if (
    // If card type is BAR or TABLE then only check groupBy specific conditons
    [CardTypes.BAR, CardTypes.TABLE].includes(type) &&
    groupBy &&
    ((groupBy.length === 1 && groupBy[0] !== 'deviceid') || groupBy.length > 1)
  ) {
    attributes
      .filter(attribute => !attribute.aggregator) // Only raw attribute without aggregator property
      .filter(attribute => !['deviceid'].includes(attribute.attribute)) // Filter out hardcoded deviceid dataItem which was added in getValidDataItems() function
      .forEach(item => {
        const dataItemName = item.attribute;
        const matchingDataItem = dataItems?.find(dataItem => dataItem.name === dataItemName);

        if (!matchingDataItem) {
          // data item not found
          errorMessage.push(
            formatMessage(dashboardUtilsMessages.errorDataItemNotFound, { dataItemName })
          );
        } else if (matchingDataItem) {
          if (isDataItemRawMetric(matchingDataItem)) {
            // V2 raw metric must have aggregation method applied while using groupBy
            errorMessage.push(
              formatMessage(dashboardUtilsMessages.errorRawMetricWithGroupBy, {
                dataItemName,
              })
            );
          } else if (isDataItemDerived(matchingDataItem)) {
            // Streaming aggregated metric cannot be shown with groupBy
            if (isDataItemCategoryAggreator(matchingDataItem)) {
              errorMessage.push(
                formatMessage(dashboardUtilsMessages.errorStreamingAggregatedMetricWithGroupBy, {
                  dataItemName,
                })
              );
            } else {
              // V2 Transformer must have aggregation method applied while using groupBy
              errorMessage.push(
                formatMessage(dashboardUtilsMessages.errorDerivedMetricWithGroupBy, {
                  dataItemName,
                })
              );
            }
          }
        }
      });
  }

  return errorMessage.length >= 1 ? errorMessage.join(', ') : undefined;
};
